import { useState } from "react";

import { useLiveData } from "hooks";
import { FirebaseShard } from "firebaseWrapper";
import { AthenaResults } from "components/athena/FetchAthenaResults";
import { HighchartPlotline } from "components/highcharts/HighchartOptions";
import { useAuthDataContext, Permissions } from "modules/auth";
export { EventList } from "./components/EventList";

export interface EventData {
  message_received: string;
  timestamp_utc: string;
  device_id: string;
  unit_id: string;
  site_id: string;
  event_text: string;
  event_code: number;
  event_level: number;
}

export const useEventData = (
  siteId: string,
  unitId: string | undefined,
  deviceId: string | undefined,
  maxItems: number
) => {
  const [latestData, setLatestData] = useState<EventData[]>([]);
  const { enforcer } = useAuthDataContext();
  const permittedCustomerEventTypes = enforcer
    .getDataFiltersFor(Permissions.Devices_ViewEvents)
    .getForField("eventType");
  const { isLoading } = useLiveData(
    {
      isPaused: false,
      maxDataPoints: unitId ? 1000 : maxItems,
      firebaseShard: FirebaseShard.Customer,
      firebaseRef: `devices/dataBySite/${siteId}/historyCombined/data/event_data`,
      orderByDescending: true,
      onInitialData: setLatestData,
      onIncrementalData: setLatestData,
    },
    [siteId]
  );
  return {
    isLoading,
    eventData: latestData
      .filter(
        (x) =>
          (!unitId || x.unit_id === unitId) &&
          (!deviceId || x.device_id === deviceId) &&
          (permittedCustomerEventTypes.$any ||
            permittedCustomerEventTypes.$in.some((v) =>
              x.event_text.includes(v)
            ))
      )
      .slice(0, maxItems)
      .sort((a, b) => {
        return Date.parse(b.timestamp_utc) - Date.parse(a.timestamp_utc);
      }),
  };
};

const event_deviceid_column_name = "event_device_id";

export const extractEventDataFromAthenaResults = (
  athenaData: AthenaResults
): EventData[] => {
  const eventDeviceIdColumn = athenaData.columns.find(
    (x) => x.name === event_deviceid_column_name
  );
  if (!eventDeviceIdColumn) return [];
  const eventColumnIndices = Object.fromEntries(
    athenaData.columns.map((x) => [x.name, x.index])
  );
  return athenaData.rawRows.reduce((prev, x, index) => {
    if (x[eventDeviceIdColumn.index])
      prev.push({
        timestamp_utc: new Date(athenaData.parsedRows[index][0]).toISOString(),
        device_id: x[eventColumnIndices["event_device_id"]],
        site_id: x[eventColumnIndices["event_site_id"]],
        unit_id: x[eventColumnIndices["event_unit_id"]],
        event_text: x[eventColumnIndices["event_text"]],
        event_code: parseInt(x[eventColumnIndices["event_code"]] || "0", 10),
        event_level: parseInt(x[eventColumnIndices["event_level"]] || "0", 10),
      } as EventData);
    return prev;
  }, [] as EventData[]);
};

export const extractPlotlinesFromEventData = (
  events: EventData[]
): HighchartPlotline[] => {
  return events.map((x) => ({
    timestamp: Date.parse(x.timestamp_utc).valueOf(),
    text:
      x.event_text || `${x.event_code} event code (no event text available)`,
  }));
};
